import MoreIcon from "@shared/svg/more.svg?react";
import { BulkSelectCheckbox } from "@shared/ui/Inputs";
import { MakulaIcon } from "@shared/ui/MakulaIcon";
import React, { JSX, useState } from "react";
import Tooltip from "@shared/ui/Tooltip";

import { WorkOrderCardAssignees } from "./WorkOrderCardAssignees";
import { WorkOrderCardIcon } from "./WorkOrderCardIcon";
import {
  WorkOrderCardDueTag,
  WorkOrderCardScheduleTag,
  WorkOrderCardStatusTag,
} from "./WorkOrderCardTags";
import { WorkOrderAssetsTooltipContent } from "./WorkOrderAssetsTooltipContent";
import BodyText, { BODY_TEXT_SIZES } from "../BodyText";
import Headline, { HEADLINE_SIZES } from "../Headline";
import { useIntl } from "react-intl";

interface Assignee {
  _id: string;
  name: string;
}

interface WorkOrderCardProps {
  /** Work order title */
  title: string;
  /** Ticket ID (e.g., #WBRW10321) */
  ticketId: string;
  /** Company name */
  companyName?: string;
  /** Asset details */
  assets?: { _id: string; name: string; serialNumber: string }[];
  /** Icon name from ticketType.icon */
  iconName?: string;
  /** Icon color from ticketType.color */
  iconColor?: string;
  /** List of assignees */
  assignees?: Assignee[];
  /** Days remaining until due (negative = overdue) */
  daysRemaining?: number;
  /** Scheduled date (ISO string) */
  scheduledDate?: string;
  /** Status label */
  statusLabel?: string;
  /** Status color */
  statusColor?: string;
  /** Notification count for the icon */
  notificationCount?: number;
  /** Number of sub work orders */
  subWorkOrderCount?: number;
  /** Show assignees section */
  showAssignees?: boolean;
  /** Show due date tag */
  showDueDate?: boolean;
  /** Show schedule tag */
  showSchedule?: boolean;
  /** Show actions menu */
  showActions?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Actions menu click handler */
  onActionsClick?: (e: React.MouseEvent) => void;
  /**
   * Custom leading component (e.g. expand button). When provided,
   * appears before the icon.
   */
  leadingComponent?: React.ReactNode;
  /**
   * Custom trailing component (e.g. ActionsPopover). When provided,
   * replaces the default actions button.
   */
  trailingComponent?: React.ReactNode;
  /** Is this an external ticket (vs internal work order) - shows external indicator) */
  isExternal?: boolean;
  /** Icon container shape: "circle" (default) or "rounded" (small radius square) */
  iconShape?: "circle" | "rounded";
  /** Is this a request ticket (vs work order) */
  isRequest?: boolean;
  /** Show Hierarchy */
  showHierarchy?: boolean;
  /** Whether this card is currently selected (bulk selection mode) */
  isSelected?: boolean;
  /**
   * When true, the leading icon is replaced by a selection checkbox on hover
   * (and permanently once selected or any item is selected). Opt-in so
   * non-selection usages keep their icon.
   */
  selectable?: boolean;
  /** Force the checkbox to stay visible (set once ≥1 item is selected). */
  anySelected?: boolean;
  /** Toggle this card's selection. */
  onToggleSelect?: () => void;
}

/**
 * WorkOrderCard
 *
 * A card component for displaying work orders/requests.
 * Shows work order icon, title, metadata, assignees, tags, and status.
 */
export const WorkOrderCard = ({
  title,
  ticketId,
  companyName,
  isRequest,
  iconName,
  assets,
  iconColor,
  assignees = [],
  daysRemaining,
  scheduledDate,
  statusLabel,
  statusColor,
  notificationCount = 0,
  subWorkOrderCount = 0,
  showAssignees = true,
  showDueDate = true,
  showSchedule = true,
  showActions = true,
  onClick,
  onActionsClick,
  leadingComponent,
  trailingComponent,
  isExternal = false,
  iconShape = "circle",
  isSelected = false,
  selectable = false,
  anySelected = false,
  onToggleSelect,
}: WorkOrderCardProps): JSX.Element => {
  const [isHovered, setIsHovered] = useState(false);
  const { messages } = useIntl();

  // Build info line pieces
  const infoParts: string[] = [];
  if (ticketId) infoParts.push(`#${ticketId}`);
  if (companyName) infoParts.push(companyName);

  const hasMultipleAssets = assets && assets.length > 1;
  const assetsTooltipId = hasMultipleAssets
    ? `assets-tooltip-${ticketId}`
    : null;

  if (assets?.length && !hasMultipleAssets) {
    infoParts.push(`${assets?.[0]?.name} • ${assets?.[0]?.serialNumber}`);
  }

  if (subWorkOrderCount > 0) {
    const subWorkOrderLabel =
      subWorkOrderCount > 1
        ? messages?.tickets?.[isRequest ? "subRequests" : "subWorkOrders"] ??
          "Sub Work Orders"
        : messages?.tickets?.[isRequest ? "subRequest" : "subWorkOrder"] ??
          "Sub Work Order";
    infoParts.push(`${subWorkOrderCount} ${subWorkOrderLabel}`);
  }

  return (
    <div
      className={`relative flex flex-col sm:flex-row items-start sm:items-center gap-lg sm:gap-2xl p-md sm:p-lg rounded-lg border border-solid border-primary cursor-pointer transition-colors duration-200 ${
        isSelected || isHovered ? "bg-accent" : "bg-primary"
      }`}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Main content row */}
      <div className="flex items-center gap-md sm:gap-lg flex-1 min-w-0 w-full">
        {/* Leading component (e.g. expand button) */}
        {leadingComponent}
        {/* Icon — swapped for a selection checkbox (same 48px slot) on hover
            or once selected, so the title never shifts. */}
        {selectable && (isHovered || isSelected || anySelected) ? (
          <BulkSelectCheckbox
            checked={isSelected}
            onToggle={() => onToggleSelect?.()}
            style={{ width: 48, height: 48 }}
          />
        ) : (
          <WorkOrderCardIcon
            iconName={iconName}
            color={iconColor}
            notificationCount={notificationCount}
            isHovered={isHovered}
            isExternal={isExternal}
            shape={iconShape}
          />
        )}

        {/* Title & Info */}
        <div className="flex flex-col gap-xs sm:gap-sm flex-1 min-w-0">
          <Headline
            size={HEADLINE_SIZES.X_SMALL}
            className="truncate"
            title={title}
          >
            {title}
          </Headline>
          <div className="flex items-center gap-2xs flex-wrap">
            {infoParts.map((part, index) => (
              <React.Fragment key={index}>
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  color="text-secondary"
                  className="truncate max-w-full sm:max-w-none"
                >
                  {part}
                </BodyText>
                {index < infoParts.length - 1 && (
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                    className="hidden sm:inline"
                  >
                    •
                  </BodyText>
                )}
              </React.Fragment>
            ))}
            {hasMultipleAssets && (
              <>
                {infoParts.length > 0 && (
                  <BodyText
                    size={BODY_TEXT_SIZES.X_SMALL}
                    color="text-secondary"
                    className="hidden sm:inline"
                  >
                    •
                  </BodyText>
                )}
                <BodyText
                  size={BODY_TEXT_SIZES.X_SMALL}
                  color="text-secondary"
                  className="truncate max-w-full sm:max-w-none"
                  data-tip
                  data-for={assetsTooltipId}
                >
                  {`${assets?.length} ${messages?.connections.single.tabs.assets}`}
                </BodyText>
                <Tooltip
                  id={assetsTooltipId}
                  place="top"
                  clickable
                  delayHide={100}
                  getContent={() => (
                    <WorkOrderAssetsTooltipContent assets={assets} />
                  )}
                />
              </>
            )}
          </div>
        </div>
        {/* Assignees - Desktop only */}
        {showAssignees && assignees.length > 0 && (
          <div className="hidden lg:block flex-shrink-0">
            <WorkOrderCardAssignees assignees={assignees} maxVisible={3} />
          </div>
        )}
      </div>

      {/* Tags & Mobile Assignees Row */}
      <div className="flex items-center justify-between gap-sm w-full sm:w-auto sm:flex-shrink-0 lg:gap-md">
        {/* Mobile/Tablet Assignees - shown only below desktop */}
        {showAssignees && assignees.length > 0 && (
          <div className="lg:hidden flex-shrink-0">
            <WorkOrderCardAssignees assignees={assignees} maxVisible={2} />
          </div>
        )}

        {/* Tags group */}
        <div className="flex items-center gap-sm flex-wrap lg:flex-nowrap ml-auto lg:ml-0">
          {showDueDate && (
            <WorkOrderCardDueTag
              daysRemaining={daysRemaining}
              isHovered={isHovered}
            />
          )}
          {showSchedule && (
            <WorkOrderCardScheduleTag
              date={scheduledDate}
              isHovered={isHovered}
            />
          )}
          {statusLabel && (
            <WorkOrderCardStatusTag
              label={statusLabel}
              color={statusColor}
              isHovered={isHovered}
            />
          )}
        </div>
      </div>

      {/* Actions - always on the right */}
      {trailingComponent ? (
        <div
          className="absolute top-md right-md sm:static sm:top-auto sm:right-auto flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {trailingComponent}
        </div>
      ) : (
        showActions && (
          <button
            className="absolute top-md right-md sm:static sm:top-auto sm:right-auto flex-shrink-0 w-3xl h-3xl flex items-center justify-center bg-transparent border-none cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              onActionsClick?.(e);
            }}
          >
            <MakulaIcon icon={<MoreIcon />} className="w-xl h-xl" />
          </button>
        )
      )}
    </div>
  );
};
